import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  FC,
  ReactNode,
} from "react";
import {
  useRestApiProvider,
  whatsappConversations$,
  instagramConversations$,
  webChatBotConversations$,
  twilioConversations$,
  emailConversations$,
} from "@jugl-web/rest-api";
import {
  CRMChannel,
  CRMUnreadCounts,
} from "@jugl-web/rest-api/integrations/types";
import { useEntityProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntityProvider";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useUserRole } from "@web-src/modules/common/hooks/useUserRole";
import { useAnonymousSession } from "@web-src/modules/auth/providers/AnonymousSessionProvider";
import { CRMUnreadIndicatorsContext } from "./CRMUnreadIndicatorsContext";
import { crmUnreadIndicators$ } from "./crmUnreadIndicators";

const IS_LIVE_RELOAD_ENABLED = false;

type CRMUnreadCountKey = keyof CRMUnreadCounts;

const channelToKeyMap: Record<CRMChannel, CRMUnreadCountKey> = {
  [CRMChannel.whatsapp]: "wa_unread",
  [CRMChannel.instagram]: "ig_unread",
  [CRMChannel.chatbot]: "chatbot_unread",
  [CRMChannel.twilio]: "twilio_unread",
  [CRMChannel.voice]: "voice_unread",
  [CRMChannel.email]: "email_unread",
};

export const CRMUnreadIndicatorsProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { entityId, entity } = useEntityProvider();
  const { integrationsApi } = useRestApiProvider();
  const [unreadCounts, setUnreadCounts] = useState<CRMUnreadCounts | null>(
    null
  );
  const { isAdmin } = useUserRole();
  const { isAnonymous } = useAnonymousSession();

  const shouldFetchUnreadCounts =
    entityId && (entity?.type === "cx" || isAdmin) && !isAnonymous;

  const { data, isLoading, isError, refetch } =
    integrationsApi.useGetCRMUnreadCountsQuery(
      shouldFetchUnreadCounts ? { entityId: entityId as string } : skipToken,
      { refetchOnMountOrArgChange: true }
    );

  useEffect(() => {
    setUnreadCounts(null);
  }, [entityId]);

  useEffect(() => {
    if (!shouldFetchUnreadCounts) {
      setUnreadCounts(null);
    } else if (data) {
      setUnreadCounts(data);
    }
  }, [shouldFetchUnreadCounts, data]);

  useEffect(() => {
    if (!IS_LIVE_RELOAD_ENABLED) return () => {};

    const subscription = crmUnreadIndicators$.subscribe((action) => {
      setUnreadCounts((prev) => {
        if (!prev) return prev;

        if (action.type === "set") {
          return action.counts;
        }

        if (action.type === "increment") {
          const key = channelToKeyMap[action.channel];
          return {
            ...prev,
            [key]: (prev[key] ?? 0) + 1,
          };
        }

        if (action.type === "decrement") {
          const key = channelToKeyMap[action.channel];
          return {
            ...prev,
            [key]: Math.max(0, (prev[key] ?? 0) - 1),
          };
        }

        return prev;
      });
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!IS_LIVE_RELOAD_ENABLED) return () => {};

    const subscriptions = [
      whatsappConversations$.subscribe((action) => {
        if (
          action.type === "prepend" &&
          !action.conversation.read &&
          action.conversation.type === "incoming"
        ) {
          crmUnreadIndicators$.next({
            type: "increment",
            channel: CRMChannel.whatsapp,
          });
          whatsappConversations$.next({
            type: "update",
            conversation: { ...action.conversation, read: false },
          });
        }
      }),
      instagramConversations$.subscribe((action) => {
        if (
          action.type === "prepend" &&
          !action.conversation.read &&
          action.conversation.type === "incoming"
        ) {
          crmUnreadIndicators$.next({
            type: "increment",
            channel: CRMChannel.instagram,
          });
          instagramConversations$.next({
            type: "update",
            conversation: { ...action.conversation, read: false },
          });
        }
      }),
      webChatBotConversations$.subscribe((action) => {
        if (
          action.type === "prepend" &&
          !action.conversation.read &&
          action.conversation.type === "incoming"
        ) {
          crmUnreadIndicators$.next({
            type: "increment",
            channel: CRMChannel.chatbot,
          });
          webChatBotConversations$.next({
            type: "update",
            conversation: { ...action.conversation, read: false },
          });
        }
      }),
      twilioConversations$.subscribe((action) => {
        if (
          action.type === "prepend" &&
          !action.conversation.read &&
          action.conversation.type === "incoming"
        ) {
          crmUnreadIndicators$.next({
            type: "increment",
            channel: CRMChannel.twilio,
          });
          twilioConversations$.next({
            type: "update",
            conversation: { ...action.conversation, read: false },
          });
        }
      }),
      emailConversations$.subscribe((action) => {
        if (
          action.type === "prepend" &&
          !action.conversation.read &&
          action.conversation.type === "incoming"
        ) {
          crmUnreadIndicators$.next({
            type: "increment",
            channel: CRMChannel.email,
          });
          emailConversations$.next({
            type: "update",
            conversation: { ...action.conversation, read: false },
          });
        }
      }),
    ];

    return () => subscriptions.forEach((sub) => sub.unsubscribe());
  }, []);

  const totalUnreadCount = useMemo(() => {
    if (!unreadCounts) return 0;
    return (
      unreadCounts.wa_unread +
      unreadCounts.ig_unread +
      unreadCounts.chatbot_unread +
      unreadCounts.twilio_unread +
      unreadCounts.voice_unread +
      unreadCounts.email_unread
    );
  }, [unreadCounts]);

  const safeRefetch = useCallback(() => {
    if (!shouldFetchUnreadCounts) return;
    refetch();
  }, [refetch, shouldFetchUnreadCounts]);

  const value = useMemo(
    () => ({
      unreadCounts,
      totalUnreadCount,
      isLoading,
      isError,
      refetch: safeRefetch,
    }),
    [unreadCounts, totalUnreadCount, isLoading, isError, safeRefetch]
  );

  return (
    <CRMUnreadIndicatorsContext.Provider value={value}>
      {children}
    </CRMUnreadIndicatorsContext.Provider>
  );
};
